import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';

/* ─────────────────────────────────────────────────────────────────────────────
   Testimonial.js  —  matches Home.js design system
   • Dark navy section (like "How It Works") for visual contrast rhythm
   • Clash Display headings, DM Sans body, same CSS variables
   • Cards with glassmorphic depth on navy, gold stars, premium highlights
   • Integrated direct Google Review Call-To-Action matching your brand layout
───────────────────────────────────────────────────────────────────────────── */

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

.flap-testimonials {
  padding: 100px 5%;
  background: #06112b;
  font-family: 'DM Sans', sans-serif;
  position: relative;
  overflow: hidden;
}

/* Same grid overlay as .flap-how */
.flap-testimonials::before {
  content: '';
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(28,115,186,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(28,115,186,0.05) 1px, transparent 1px);
  background-size: 52px 52px;
  pointer-events: none;
}

/* Glow orbs */
.flap-testimonials::after {
  content: '';
  position: absolute;
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(28,115,186,0.12) 0%, transparent 70%);
  top: -100px; right: -100px;
  pointer-events: none;
}

.flap-testimonials-inner {
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

/* Section label — matches Home.js .section-label on navy */
.test-section-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #f5a623;
  margin-bottom: 12px;
}

.test-section-heading {
  font-family: 'Clash Display', sans-serif;
  font-size: clamp(1.8rem, 3vw + 0.5rem, 2.8rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  margin-bottom: 56px;
}
.test-section-heading em {
  font-style: normal;
  color: #5aaef7;
}

/* Swiper overflow */
.test-swiper-wrap {
  margin: 0 -5%;
  padding: 0 5% 24px;
  overflow: hidden;
}
.test-swiper-wrap .swiper { overflow: visible !important; }

/* ── Card ── */
.test-card {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 20px;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s, background 0.3s, transform 0.3s;
  backdrop-filter: blur(12px);
  cursor: default;
}
.test-card:hover {
  border-color: rgba(28,115,186,0.35);
  background: rgba(255,255,255,0.07);
  transform: translateY(-5px);
}
/* Active slide highlight */
.swiper-slide-active .test-card {
  border-color: rgba(28,115,186,0.3);
  background: rgba(28,115,186,0.08);
}

/* Top shimmer accent */
.test-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #1c73ba, #5aaef7, transparent);
  opacity: 0;
  transition: opacity 0.3s;
}
.test-card:hover::before,
.swiper-slide-active .test-card::before { opacity: 1; }

/* Big decorative quote mark */
.test-quote-mark {
  position: absolute;
  top: 16px; right: 20px;
  font-family: 'Clash Display', sans-serif;
  font-size: 5rem;
  line-height: 1;
  color: rgba(28,115,186,0.15);
  user-select: none;
  pointer-events: none;
}

/* ── Stars ── */
.test-stars {
  display: flex; gap: 3px;
  margin-bottom: 16px;
}
.test-star {
  width: 16px; height: 16px;
}
.test-star.filled  { fill: #f5a623; }
.test-star.empty   { fill: rgba(255,255,255,0.15); }

/* ── Review text ── */
.test-review-text {
  font-size: 0.92rem;
  line-height: 1.75;
  color: rgba(255,255,255,0.7);
  flex: 1;
  margin-bottom: 24px;
  font-style: italic;
  position: relative;
  z-index: 1;
}

/* ── Author row ── */
.test-author {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
}
.test-author-img {
  width: 46px; height: 46px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(28,115,186,0.4);
  flex-shrink: 0;
}
.test-author-initials {
  width: 46px; height: 46px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1c73ba, #2b8de0);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Clash Display', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  border: 2px solid rgba(28,115,186,0.4);
}
.test-author-name {
  font-family: 'Clash Display', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.2;
}
.test-author-pos {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.4);
  margin-top: 2px;
}
.test-verified {
  margin-left: auto;
  display: flex; align-items: center; gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #5aaef7;
  white-space: nowrap;
}

/* ── Summary row below swiper ── */
.test-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  margin-top: 52px;
  margin-bottom: 64px;
  flex-wrap: wrap;
}
.test-summary-item {
  text-align: center;
}
.test-summary-num {
  font-family: 'Clash Display', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}
.test-summary-num span { color: #f5a623; }
.test-summary-label {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 4px;
}
.test-summary-divider {
  width: 1px; height: 40px;
  background: rgba(255,255,255,0.08);
}

/* ── Google CTA Card ── */
.test-google-cta {
  background: linear-gradient(135deg, rgba(28, 115, 186, 0.15) 0%, rgba(6, 17, 43, 0.8) 100%);
  border: 1px solid #1c73ba;
  border-radius: 20px;
  padding: 40px 30px;
  text-align: center;
  max-width: 680px;
  margin: 0 auto;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
}
.test-google-title {
  font-family: 'Clash Display', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 10px;
}
.test-google-desc {
  color: #94a3b8;
  font-size: 0.88rem;
  max-width: 480px;
  margin: 0 auto 24px;
  line-height: 1.6;
}
.test-google-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: #1c73ba;
  color: #ffffff;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 20px rgba(28, 115, 186, 0.4);
}
.test-google-btn:hover {
  background: #155a94;
  transform: translateY(-2px);
}

@media (max-width: 480px) {
  .test-summary-divider { display: none; }
  .test-summary { gap: 24px; }
  .test-google-cta { padding: 32px 20px; }
}
`;

const reviews = [
  {
    "review": "आफ्नो व्यवसाय प्रबर्द्धन गर्नको लागि यो Flap Tool एकदम उपयोगी र लाभदायिक छ एकपटक सबैले प्रयोग गर्नुहोला ।",
    "image": "./images/user-data/sankalpadhungana.jpeg",
    "name": "Sankalpa Dhungana",
    "position": "",
    "rating": 5
  },
  {
    "review": "Step into Future with Flap products.!",
    "image": "./images/user-data/eliza_sapkota.png",
    "name": "Eliza Sapkota",
    "position": "",
    "rating": 5
  },
  {
    "review": "I absolutey loving it!!!",
    "image": "./images/user-data/janardhan_shilwal.png",
    "name": "Janardhan Silwal",
    "position": "",
    "rating": 5
  },
  {
    "review": "Flap प्रयोग गर्न सजिलो, आकर्षक र भरपर्दो सम्पर्क share गर्ने एप हो, जहाँ QR कोडबाट तुरुन्तै कनेक्ट हुन सकिन्छ, जसले नयाँ सम्पर्कहरूलाई जोड्न एकदम सरल र रमाइलो बनाएको छ – एकचोटि अवश्य प्रयोग गरेर हेर्नुहोस् !",
    "image": "./images/user-data/santoshtimalsena.jpeg",
    "name": "Santosh Timalsena",
    "position": "",
    "rating": 5
  },
  {
    "review": "Very convenient and technology friendly. This is future👏",
    "image": "./images/user-data/sujana_kulung.png",
    "name": "Sujana Kulung",
    "position": "",
    "rating": 5
  },
  {
    "review": "I have been using flap wallet for a year. It feels innovative and exciting to share contact. Best wishes further.",
    "image": "./images/user-data/saugat_tiwari.png",
    "name": "Saugat Tiwari",
    "position": "",
    "rating": 5
  },
  {
    "review": "Their cards are seamless and of a premium quality. Very satisfied with the product.!",
    "image": "./images/user-data/kishor_gartaula.png",
    "name": "Kishor Gartaula",
    "position": "",
    "rating": 5
  },
  {
    "review": "quality product with reasonable price",
    "image": "",
    "name": "Nitin Shrestha",
    "position": "",
    "rating": 5
  },
  {
    "review": "I love this new and exciting way of meeting",
    "image": "",
    "name": "Sangam Thapa",
    "position": "",
    "rating": 5
  },
  {
    "review": "Very handy!! Loved it",
    "image": "",
    "name": "Nayan Bakhadyo",
    "position": "",
    "rating": 5
  },
  {
    "review": "This app is very useful but you have to scan it, it doesn't work as soon as you touch it on all mobiles, but it's perfect for paperless visiting cards.",
    "image": "./images/user-data/devraj_jaishi.jpeg",
    "name": "Devraj Jaishi",
    "position": "Karnali Rafting And Adventure Pvt Ltd",
    "rating": 5
  },
  {
    "review": "I’ve been using the Flap digital business card for a six months now, and I must say it’s a smart and eco-friendly alternative to traditional paper cards. The QR-based access makes it super convenient, just one scan and all my business details, social links, and portfolio are instantly available. It’s especially useful when networking at events or meetings, where sharing contacts quickly matters.",
    "image": "./images/user-data/pradip_timsina.png",
    "name": "Pradip Timsina",
    "position": "Think Tank Info Tech Pvt. Ltd.",
    "rating": 5
  },
  {
    "review": "I have been using flap card since last year and it has been useful for contacting sharing along with business and social media details. Extremely recommend if you’re not aware about contact sharing and NFC card.",
    "image": "./images/user-data/bishal_dhakal.png",
    "name": "Bishal Dhakal",
    "position": "Gandaki Solution Pvt. Ltd.",
    "rating": 5
  },
  {
    "review": "The product is fantastic with amazing printing quality also the service is top notch.Most recommend with a great price point for product we are getting.",
    "image": "./images/user-data/ishan_katuwal.png",
    "name": "Ishan Katuwal",
    "position": "",
    "rating": 5
  },
  {
    "review": "Really impressed with Flap Card. It’s simple, smart, and saves time. Loved how easy it was to share my info—no app, just tap or scan. Honestly, feels like the future of networking. Keep it up",
    "image": "./images/user-data/samikshya_dhakal.png",
    "name": "Samikshya Dhakal",
    "position": " ",
    "rating": 5
  },
  {
    "review": "Very portable and good product I would like to say after using it for sometime,I would like others to try it once.",
    "image": "./images/user-data/roshan_poudel.png",
    "name": "Roshan Poudel",
    "position": " ",
    "rating": 5
  },
  {
    "review": "I have been using Flap Card through the Flap Android Beta app. It was a proud moment for me to be a testing user of the Flap contact sharing app. This app has been very useful for contact sharing and management. I would definitely recommend everyone to try this app. It will help you manage contacts and content in a new way.",
    "image": "./images/user-data/sangita_dhungana.png",
    "name": "Sangita Dhungana",
    "position": " ",
    "rating": 5
  }
];

function StarRating({ rating }) {
  return (
    <div className="test-stars">
      {Array.from({ length: 5 }, (_, i) => (
        <svg key={i} className={`test-star ${i < rating ? 'filled' : 'empty'}`}
          viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.5 14.25l-5.584 2.936 1.066-6.218L.465 6.564l6.243-.907L9.5 0l2.792 5.657 6.243.907-4.517 4.404 1.066 6.218" />
        </svg>
      ))}
    </div>
  );
}

export default function Testimonial() {
  // Verified Live Direct Google Review Link for Chunidevi Multitech
  const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJT2m8h70Z6zkRZg5h_d7OwnY";

  return (
    <section className="flap-testimonials">
      <style>{STYLES}</style>
      <div className="flap-testimonials-inner">

        {/* Heading */}
        <span className="test-section-label">Happy Users</span>
        <h2 className="test-section-heading">
          What our customers <em>actually say</em>
        </h2>

        {/* Swiper */}
        <div className="test-swiper-wrap">
          <Swiper
            modules={[Autoplay, FreeMode]}
            autoplay={{ delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true }}
            freeMode={{ enabled: true, momentum: true }}
            speed={2000}
            loop={true}
            slidesPerView={1.15}
            spaceBetween={20}
            centeredSlides={true}
            breakpoints={{
              640:  { slidesPerView: 2,    spaceBetween: 24, centeredSlides: false },
              1024: { slidesPerView: 3,    spaceBetween: 28, centeredSlides: false },
            }}
          >
            {reviews.map((r, i) => (
              <SwiperSlide key={i}>
                <div className="test-card">
                  <span className="test-quote-mark">"</span>

                  <StarRating rating={r.rating} />

                  <p className="test-review-text">"{r.review}"</p>

                  <div className="test-author">
                    {r.image ? (
                      <img
                        className="test-author-img"
                        src={r.image}
                        alt={r.name}
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="test-author-initials">
                        {r.name.trim().charAt(0)}
                      </div>
                    )}
                    <div>
                      <div className="test-author-name">{r.name}</div>
                      {r.position && <div className="test-author-pos">{r.position}</div>}
                    </div>
                    <div className="test-verified">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Verified
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Summary stats */}
        <div className="test-summary">
          {[
            { num: '5.0', suffix: '★', label: 'Average Rating' },
            null,
            { num: '500', suffix: '+', label: 'Happy Users' },
            null,
            { num: '100', suffix: '%', label: 'Recommend Flap' },
          ].map((item, i) =>
            item === null
              ? <div key={i} className="test-summary-divider" />
              : (
                <div key={i} className="test-summary-item">
                  <div className="test-summary-num">{item.num}<span>{item.suffix}</span></div>
                  <div className="test-summary-label">{item.label}</div>
                </div>
              )
          )}
        </div>

        {/* Google Review Direct Call-to-Action Card Block */}
        <div className="test-google-cta">
          <h3 className="test-google-title">Worked with us recently?</h3>
          <p className="test-google-desc">
            Drop a direct evaluation on our verified Google Maps profile! Your feedback helps us optimize smart features and premium hardware structures.
          </p>
          <a 
            href={GOOGLE_REVIEW_URL} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="test-google-btn"
          >
            <span>✍️ Leave a Review on Google</span>
          </a>
        </div>

      </div>
    </section>
  );
}